/**
 * Module: screens/pantene/ModerationStatus
 * Role: Polls /pantene/video/{slot_id}/status and renders stage progress
 *       plus final verdict UI (pass / gray / fail / infrastructure error).
 *
 * Uses: api/pantene:usePollStatus, react-router-dom,
 *       screens/pantene/_pantene-atoms
 * Used by: App.tsx route /pantene/:slot_id/status
 */

import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePollStatus } from '../../api/pantene';
import type { JobStage } from '../../api/pantene';
import { IOSScreen, PageHeader, PressBtn, EmptyState, Ic } from '../../components/atoms';
import { P, S, T, G } from '../../styles/tokens';
import { Spinner, StageStrip, STAGE_LABELS, ReasonsList } from './_pantene-atoms';

const POLL_TIMEOUT_MS = 5 * 60 * 1000;

// ─── Local sub-components ────────────────────────────────────────────────────
function VerdictCard({ bg, border, children }: { bg: string; border: string; children: React.ReactNode }) {
  return (
    <div style={{ borderRadius: 20, background: bg, border, padding: '20px 16px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      {children}
    </div>
  );
}

function VIcon({ name, iconColor, bg }: { name: 'check' | 'clock' | 'warning' | 'xmark'; iconColor: string; bg: string }) {
  return (
    <div style={{ width: 56, height: 56, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Ic name={name} size={26} color={iconColor} />
    </div>
  );
}

export default function ModerationStatus() {
  const { slot_id: slotId } = useParams<{ slot_id: string }>();
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data, error, isLoading, refetch } = usePollStatus(slotId ?? '');

  useEffect(() => {
    timeoutRef.current = setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  useEffect(() => {
    if (data?.stage === 'done' || data?.stage === 'failed')
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, [data?.stage]);

  const handleReupload = () => { setTimedOut(false); navigate(`/pantene/${slotId}/upload`); };

  const centeredBox = {
    position: 'absolute' as const, top: 96, bottom: 0, left: 0, right: 0,
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
    justifyContent: 'center', padding: '0 24px', gap: 20,
  };

  if (isLoading && !data) {
    return (
      <IOSScreen>
        <PageHeader title="Статус проверки" onBack={() => navigate(-1)} />
        <div style={centeredBox}><Spinner size={40} /><span className="sr-only">Загрузка...</span></div>
      </IOSScreen>
    );
  }

  if (error && !data) {
    return (
      <IOSScreen>
        <PageHeader title="Статус проверки" onBack={() => navigate(-1)} />
        <div style={centeredBox}>
          <EmptyState icon={<Ic name="warning" size={28} color={P.danger} />}
            title="Не удалось получить статус" subtitle="Проверьте соединение и попробуйте снова." />
          <PressBtn onClick={() => void refetch()}>Обновить</PressBtn>
        </div>
      </IOSScreen>
    );
  }

  const stage = (data?.stage ?? 'queued') as JobStage;
  const verdict = data?.verdict ?? null;
  const reasons = data?.reasons ?? [];
  const retryAllowed = data?.retry_allowed ?? false;
  const requiresHumanReview = data?.requires_human_review ?? false;

  if (stage === 'failed') {
    return (
      <IOSScreen>
        <PageHeader title="Статус проверки" onBack={() => navigate(-1)} />
        <div style={centeredBox}>
          <EmptyState icon={<Ic name="warning" size={28} color={P.danger} />}
            title="Не удалось проанализировать видео" subtitle="Попробуйте загрузить видео ещё раз." />
          <PressBtn onClick={handleReupload}>Попробовать ещё раз</PressBtn>
        </div>
      </IOSScreen>
    );
  }

  if (timedOut && stage !== 'done') {
    return (
      <IOSScreen>
        <PageHeader title="Статус проверки" onBack={() => navigate(-1)} />
        <div style={centeredBox}>
          <EmptyState icon={<Ic name="clock" size={28} color={P.warn} />}
            title="Анализ занимает дольше обычного" subtitle="Обновите страницу через минуту." />
          <PressBtn onClick={() => { setTimedOut(false); void refetch(); }}>Обновить</PressBtn>
        </div>
      </IOSScreen>
    );
  }

  if (stage !== 'done') {
    return (
      <IOSScreen>
        <PageHeader title="Статус проверки" onBack={() => navigate(-1)} />
        <StageStrip current={stage} />
        <div style={{ padding: '12px 16px 0' }}>
          <div style={{ borderRadius: 16, ...G.tinted, padding: '20px 16px',
            display: 'flex', alignItems: 'center', gap: 14 }}>
            <Spinner color={P.terracotta} size={28} />
            <p style={{ margin: 0, font: T.subhead, color: S.label }}>
              {STAGE_LABELS[stage]}... обычно занимает 1–2 минуты
            </p>
          </div>
        </div>
      </IOSScreen>
    );
  }

  const isPass = verdict === 'pass' && !requiresHumanReview;
  const isGray = verdict === 'gray' || requiresHumanReview;
  const isSoftFail = verdict === 'fail' && retryAllowed;

  if (isPass) {
    return (
      <IOSScreen>
        <PageHeader title="Результат проверки" onBack={() => navigate(-1)} />
        <StageStrip current="done" />
        <div style={{ padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <VerdictCard bg="rgba(47,143,111,0.08)" border="1px solid rgba(47,143,111,0.22)">
            <VIcon name="check" iconColor={P.success} bg="rgba(47,143,111,0.14)" />
            <p style={{ margin: 0, font: T.title2, color: P.success, textAlign: 'center' }}>Видео одобрено!</p>
            <p style={{ margin: 0, font: T.subhead, color: S.secondaryLabel, textAlign: 'center' }}>
              Можно публиковать на WB / VK / TikTok.
            </p>
          </VerdictCard>
          <PressBtn bg={P.success} onClick={() => navigate(`/flow/${slotId}`)}>Перейти к публикации</PressBtn>
        </div>
      </IOSScreen>
    );
  }

  if (isGray) {
    return (
      <IOSScreen>
        <PageHeader title="Результат проверки" onBack={() => navigate(-1)} />
        <StageStrip current="done" />
        <div style={{ padding: '16px 16px 32px' }}>
          <VerdictCard bg="rgba(197,138,46,0.08)" border="1px solid rgba(197,138,46,0.22)">
            <VIcon name="clock" iconColor={P.warn} bg="rgba(197,138,46,0.14)" />
            <p style={{ margin: 0, font: T.title2, color: P.warn, textAlign: 'center' }}>Ручная проверка</p>
            <p style={{ margin: 0, font: T.subhead, color: S.secondaryLabel, textAlign: 'center' }}>
              Проверяем вручную — до 2 часов в рабочее время.
            </p>
          </VerdictCard>
        </div>
      </IOSScreen>
    );
  }

  if (isSoftFail) {
    return (
      <IOSScreen>
        <PageHeader title="Результат проверки" onBack={() => navigate(-1)} />
        <StageStrip current="done" />
        <div style={{ padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <VerdictCard bg="rgba(184,80,54,0.07)" border="1px solid rgba(184,80,54,0.20)">
            <VIcon name="warning" iconColor={P.danger} bg="rgba(184,80,54,0.12)" />
            <p style={{ margin: 0, font: T.title2, color: P.danger, textAlign: 'center' }}>Видео не прошло проверку</p>
          </VerdictCard>
          <ReasonsList reasons={reasons} color={P.danger} />
          <PressBtn onClick={handleReupload}>Загрузить новое видео</PressBtn>
        </div>
      </IOSScreen>
    );
  }

  // Hard fail (verdict === 'fail' && !retryAllowed)
  return (
    <IOSScreen>
      <PageHeader title="Результат проверки" onBack={() => navigate(-1)} />
      <StageStrip current="done" />
      <div style={{ padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <VerdictCard bg="rgba(184,80,54,0.07)" border="1px solid rgba(184,80,54,0.20)">
          <VIcon name="xmark" iconColor={P.danger} bg="rgba(184,80,54,0.12)" />
          <p style={{ margin: 0, font: T.title2, color: P.danger, textAlign: 'center' }}>Видео отклонено</p>
        </VerdictCard>
        <ReasonsList reasons={reasons} color={P.danger} />
        <p style={{ margin: 0, font: T.footnote, color: S.secondaryLabel, textAlign: 'center' }}>
          Свяжитесь с оператором для получения помощи.
        </p>
      </div>
    </IOSScreen>
  );
}
